import React, { useState, useEffect } from "react";
import { apiService } from "../../services/apiService";
import Toast from "../ui/Toast";

const RemoveDuplicateAddressesModal = ({ onClose, onSuccess, tenant, tenantId }) => {
  const [loading, setLoading] = useState(true);
  const [previewData, setPreviewData] = useState(null);
  const [error, setError] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [toast, setToast] = useState({ message: null, type: "success" });

  const effectiveTenantId =
    tenantId || tenant?.id || tenant?.tenant_id || tenant?.tenantId;

  useEffect(() => {
    if (effectiveTenantId) {
      fetchPreview();
    } else {
      setError("Tenant ID is required");
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveTenantId]);

  const fetchPreview = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.getDedupeAddressesPreview(
        effectiveTenantId
      );

      if (response.success) {
        setPreviewData(response.data);
      } else {
        setError(response.message || "Failed to load preview");
      }
    } catch (err) {
      console.error("Error fetching dedupe preview:", err);
      setError(err.message || "Failed to load preview");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!effectiveTenantId) {
      setToast({
        message: "Tenant ID is required to remove duplicate addresses",
        type: "error",
      });
      return;
    }

    setRemoving(true);

    try {
      const response = await apiService.dedupeAddresses(effectiveTenantId);

      if (response.success) {
        const deleted =
          response.data?.deletedCount ?? response.data?.deleted_count ?? 0;
        setToast({
          message:
            deleted > 0
              ? "Duplicate addresses removed successfully"
              : "No duplicate addresses found to remove",
          type: "success",
        });
        setRemoving(false);
        setTimeout(() => {
          onSuccess(response.data);
          onClose();
        }, 800);
      } else {
        setToast({
          message: response.message || "Failed to remove duplicates",
          type: "error",
        });
        setRemoving(false);
      }
    } catch (err) {
      console.error("Error removing duplicate addresses:", err);
      setToast({
        message: err.message || "Failed to remove duplicates",
        type: "error",
      });
      setRemoving(false);
    }
  };

  const primaryBtnClass =
    tenant?.primaryBgColor || "bg-blue-600 hover:bg-blue-700";

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
          <h3 className="text-lg font-bold text-gray-900 mb-4">
            Remove duplicate addresses
          </h3>

          {loading && (
            <div className="text-center py-4">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              <p className="mt-2 text-sm text-gray-600">Loading...</p>
            </div>
          )}

          {error && (
            <div className="mb-4">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {!loading && !error && previewData && (
            <div className="mb-6">
              {previewData.addressesToRemove === 0 ? (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded">
                  <p className="text-sm text-blue-800">
                    No exact duplicate raw addresses were found for your
                    tenant.
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-gray-700 mb-3">
                    This will remove{" "}
                    <strong className="text-gray-900">
                      {previewData.addressesToRemove}
                    </strong>{" "}
                    extra duplicate row(s) across{" "}
                    <strong className="text-gray-900">
                      {previewData.duplicateGroups}
                    </strong>{" "}
                    duplicate group(s). One address is kept per unique raw
                    address (including addresses with no circuits).
                  </p>
                  <div className="bg-gray-50 p-3 rounded border border-gray-200 space-y-2 text-sm">
                    <div>
                      <span className="font-semibold text-gray-700">
                        Circuit links to reassign:{" "}
                      </span>
                      <span className="text-gray-900 font-bold">
                        {previewData.totalReassignments ?? 0}
                      </span>
                    </div>
                  </div>
                  {previewData.sampleGroups?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-medium text-gray-600 mb-1">
                        Examples:
                      </p>
                      <ul className="text-xs text-gray-700 list-disc pl-4 space-y-0.5">
                        {previewData.sampleGroups.map((g, i) => (
                          <li key={i}>
                            {g.label} ({g.duplicateCount} → keep 1)
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={removing}
              className="inline-flex items-center justify-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded shadow-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={
                removing ||
                loading ||
                error ||
                !previewData ||
                previewData.addressesToRemove === 0 ||
                !effectiveTenantId
              }
              className={`inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white ${primaryBtnClass} disabled:bg-gray-400 disabled:cursor-not-allowed`}
            >
              {removing ? "Removing..." : "Remove duplicates"}
            </button>
          </div>
        </div>
      </div>
      {toast.message && (
        <div className="fixed top-4 right-4 z-[10000]">
          <Toast
            message={toast.message}
            type={toast.type}
            portal={false}
            className="!relative !top-0 !right-0"
            onClose={() => setToast({ message: null, type: "success" })}
          />
        </div>
      )}
    </>
  );
};

export default RemoveDuplicateAddressesModal;
